/* eslint-disable */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/api";

const AuthContext = createContext(null);

// Safe JSON parse — never crashes, returns null on failure
function safeJsonParse(str) {
  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === "object" && parsed.role ? parsed : null;
  } catch {
    return null;
  }
}

// Decode JWT expiry without external dependency
function getTokenExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : 0; // ms
  } catch {
    return 0;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // BUG 1.1 FIX: Protected JSON.parse + BUG 1.4 FIX: Token expiry check at mount
  useEffect(() => {
    const stored = localStorage.getItem("hana_user");
    const storedToken = localStorage.getItem("hana_access_token");
    const storedRefresh = localStorage.getItem("hana_refresh_token");
    const patientSessionToken = localStorage.getItem("patientSessionToken");

    if (!stored || !storedToken) {
      if (patientSessionToken) {
        api.post("/auth/patient/session-login", { sessionToken: patientSessionToken })
          .then(res => {
            const { user: sessionUser, accessToken, refreshToken } = res.data;
            localStorage.setItem("hana_user", JSON.stringify(sessionUser));
            localStorage.setItem("hana_access_token", accessToken);
            localStorage.setItem("hana_refresh_token", refreshToken);
            setUser(sessionUser);
            setToken(accessToken);
          })
          .catch((err) => { if (err?.response) localStorage.removeItem("patientSessionToken"); })
          .finally(() => setLoading(false));
        return;
      }
      // Storage vidé mais cookies httpOnly encore valides (PWA, nettoyage navigateur) → restauration par cookie
      api.post("/auth/refresh", {}, { skipAuth: true })
        .then(async (res) => {
          const accessToken = res.data?.accessToken;
          if (!accessToken) return;
          const me = await api.get("/auth/me", { headers: { Authorization: `Bearer ${accessToken}` } });
          localStorage.setItem("hana_user", JSON.stringify(me.data));
          localStorage.setItem("hana_access_token", accessToken);
          if (res.data?.refreshToken) localStorage.setItem("hana_refresh_token", res.data.refreshToken);
          setUser(me.data);
          setToken(accessToken);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
      return;
    }

    // Safe parse — corrupted data → clean slate
    const parsedUser = safeJsonParse(stored);
    if (!parsedUser) {
      localStorage.removeItem("hana_user");
      localStorage.removeItem("hana_access_token");
      localStorage.removeItem("hana_refresh_token");
      setLoading(false);
      return;
    }

    // Check token expiry
    const expiry = getTokenExpiry(storedToken);
    const now = Date.now();
    const bufferMs = 60 * 1000; // 1 minute buffer

    if (expiry > now + bufferMs) {
      // Token still valid
      setUser(parsedUser);
      setToken(storedToken);
      setLoading(false);
    } else if (storedRefresh) {
      // Token expired — attempt silent refresh
      api.post("/auth/refresh", { refreshToken: storedRefresh }, { skipAuth: true })
        .then(res => {
          const { accessToken, refreshToken: rotatedRefresh } = res.data;
          localStorage.setItem("hana_access_token", accessToken);
          if (rotatedRefresh) localStorage.setItem("hana_refresh_token", rotatedRefresh);
          setUser(parsedUser);
          setToken(accessToken);
        })
        .catch((err) => {
          if (!err?.response) {
            // Hors ligne / serveur injoignable : on garde la session, le refresh se rejouera au premier appel
            setUser(parsedUser);
            setToken(storedToken);
            return;
          }
          // Refresh refusé par le serveur — clean slate
          localStorage.removeItem("hana_user");
          localStorage.removeItem("hana_access_token");
          localStorage.removeItem("hana_refresh_token");
        })
        .finally(() => setLoading(false));
    } else {
      // No refresh token — clean slate
      localStorage.removeItem("hana_user");
      localStorage.removeItem("hana_access_token");
      localStorage.removeItem("hana_refresh_token");
      setLoading(false);
    }
  }, []);

  const login = useCallback((userData, accessToken, refreshToken) => {
    localStorage.setItem("hana_user", JSON.stringify(userData));
    localStorage.setItem("hana_access_token", accessToken);
    localStorage.setItem("hana_refresh_token", refreshToken);
    setUser(userData);
    setToken(accessToken);
  }, []);

  // BUG 1.2 FIX: Complete logout — clear ALL auth artifacts + server-side cookie clear
  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem("hana_refresh_token");
      const patientSessionToken = localStorage.getItem("patientSessionToken");
      if (patientSessionToken && user?.role === "PATIENT") {
        await api.post("/auth/patient/logout", { sessionToken: patientSessionToken });
      } else {
        await api.post("/auth/logout", refreshToken ? { refreshToken } : {});
      }
    } catch {
      // Ignore — server may be unreachable
    }
    localStorage.removeItem("hana_user");
    localStorage.removeItem("hana_access_token");
    localStorage.removeItem("hana_refresh_token");
    localStorage.removeItem("patientSessionToken");
    sessionStorage.removeItem("hana_otp_pending");
    setUser(null);
    setToken(null);
  }, [user?.role]);

  const updateUser = useCallback((updates) => {
    const updated = { ...user, ...updates };
    localStorage.setItem("hana_user", JSON.stringify(updated));
    setUser(updated);
  }, [user]);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      updateUser(res.data);
    } catch (err) {
      logout();
    }
  }, [updateUser, logout]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      logout,
      updateUser,
      refreshUser,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function resolvePatientLanguage(user) {
  if (user?.role !== "PATIENT") return null;
  const raw = String(user.preferredLanguage || "").toLowerCase();
  return raw.includes("ar") || raw.includes("arab") || raw.includes("عرب") ? "ar" : "fr";
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
